import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

interface AuthUser {
  name?: string;
  email?: string;
  userType?: string;
}

function getAuthUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem("auth_user");
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(getAuthUser());
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    // Re-check auth on every navigation
    setUser(getAuthUser());
  }, [location.pathname]);

  const navLinks = [
    { label: "Anasayfa", path: "/" },
    { label: "İlanlar", path: "/ilanlar" },
    { label: "Nasıl Çalışır?", path: "/#nasil-calisir" },
  ];

  const logout = () => {
    localStorage.removeItem("auth_user");
    setUser(null);
    window.location.reload();
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-soft border-b border-surface-200"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <img
              src="https://public.readdy.ai/ai/img_res/58981b16-d987-4368-9007-d3ade5fecfc1.png"
              alt="Arsa Platformu"
              className="w-9 h-9 md:w-10 md:h-10 object-contain"
            />
            <span
              className={`text-lg md:text-xl font-bold tracking-tight transition-colors ${
                scrolled ? "text-surface-900" : "text-surface-900"
              }`}
            >
              Arsam.com
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-primary-500 ${
                  location.pathname === link.path
                    ? "text-primary-500"
                    : "text-surface-600"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/ilanlar"
              className="w-9 h-9 flex items-center justify-center rounded-full text-surface-500 hover:text-primary-500 hover:bg-primary-50 transition-colors"
              aria-label="Ara"
            >
              <i className="ri-search-line text-lg" />
            </Link>

            {user ? (
              <>
                <Link
                  to="/panelim"
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-surface-600 hover:text-primary-500 hover:bg-primary-50 rounded-full transition-colors whitespace-nowrap"
                >
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 text-xs font-bold">
                    {user.name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                  İlanlarım
                </Link>
                <button
                  onClick={logout}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-surface-500 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors whitespace-nowrap"
                >
                  <i className="ri-logout-box-r-line" />
                  Çıkış
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/giris"
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-surface-600 hover:text-primary-500 hover:bg-primary-50 rounded-full transition-colors whitespace-nowrap"
                >
                  <i className="ri-user-line text-lg" />
                  Giriş Yap
                </Link>
                <Link
                  to="/kayit"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-surface-800 text-white text-sm font-semibold rounded-full hover:bg-surface-900 transition-colors whitespace-nowrap"
                >
                  Kayıt Ol
                </Link>
              </>
            )}

            <Link
              to="/ilan-ver"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Ücretsiz İlan Ver
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-surface-700 hover:bg-surface-100 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menü"
          >
            <i
              className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-surface-100 shadow-medium">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? "bg-primary-50 text-primary-600"
                    : "text-surface-600 hover:bg-surface-50"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-surface-100 mt-3 space-y-2">
              <Link
                to="/ilanlar"
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-surface-600 hover:bg-surface-50"
              >
                <i className="ri-search-line text-lg text-surface-400" />
                İlan Ara
              </Link>

              {user ? (
                <>
                  <Link
                    to="/panelim"
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-surface-600 hover:bg-surface-50"
                  >
                    <div className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 text-xs font-bold">
                      {user.name?.charAt(0)?.toUpperCase() || "U"}
                    </div>
                    İlanlarım
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                    }}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 w-full text-left"
                  >
                    <i className="ri-logout-box-r-line text-lg text-red-400" />
                    Çıkış
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/giris"
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-surface-600 hover:bg-surface-50"
                  >
                    <i className="ri-user-line text-lg text-surface-400" />
                    Giriş Yap
                  </Link>
                  <Link
                    to="/kayit"
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-surface-600 hover:bg-surface-50"
                  >
                    <i className="ri-user-add-line text-lg text-surface-400" />
                    Kayıt Ol
                  </Link>
                </>
              )}

              <Link
                to="/ilan-ver"
                className="flex items-center justify-center gap-2 px-4 py-3 bg-primary-500 text-white text-sm font-semibold rounded-xl hover:bg-primary-600 transition-colors"
              >
                Ücretsiz İlan Ver
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
